// Centralized color computation engine with dependency tracking and caching
// Computes static, linked, and contrast colors using a dependency graph.

import { parseColor } from './colorUtils.js';
import { calculateLinkedColor, calculateContrastColor } from './linkedColorUtils.js';

function invalidColor() {
  return { isValid: false, hex: '#808080', oklch: null };
}

/**
 * Build dependency graph for a list of colors
 * Nodes are color ids; edges point from dependency -> dependent
 * e.g. if A references B, then edge B -> A
 */
function buildDependencyGraph(colors) {
  const dependents = new Map(); // key: colorId, value: Set of dependent colorIds
  const colorById = new Map();

  for (const color of colors) {
    colorById.set(color.id, color);
  }

  const addEdge = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    if (!dependents.has(fromId)) dependents.set(fromId, new Set());
    dependents.get(fromId).add(toId);
  };

  for (const color of colors) {
    if (color.mode === 'linked' && color.referenceColorId) {
      addEdge(color.referenceColorId, color.id);
    } else if (color.mode === 'contrast') {
      addEdge(color.backgroundColorId, color.id);
      addEdge(color.foregroundColorId, color.id);
    }
  }

  return { dependents, colorById };
}

/**
 * Compute a single color. `resolvedById` is the engine cache of already
 * computed dependencies — linked/contrast solvers read from it instead of
 * recursing the raw color list.
 */
function computeColor(color, allColors, colorById, contrastAlgorithm, colorSpace, resolvedById) {
  if (!color) return invalidColor();

  const colorLookup = colorById || allColors;

  if (color.mode === 'static') {
    return parseColor(color.colorValue || color.hexValue || '#808080', colorSpace);
  }
  if (color.mode === 'linked') {
    return calculateLinkedColor(
      colorLookup,
      color.referenceColorId,
      color.lExpression,
      color.cExpression,
      color.hExpression,
      color.aExpression,
      0,
      contrastAlgorithm,
      colorSpace,
      resolvedById
    );
  }
  if (color.mode === 'contrast') {
    return calculateContrastColor(
      colorLookup,
      color.backgroundColorId,
      color.foregroundColorId,
      color.contrastLevel || 60,
      color.useAlpha || false,
      contrastAlgorithm,
      colorSpace,
      resolvedById
    );
  }
  return parseColor(color.colorValue || color.hexValue || '#808080', colorSpace);
}

/**
 * Create a color engine instance for a set of colors.
 * API:
 * - computeAll(): returns Map<id, computed>
 * - computeAffected(changedIds): returns Map<id, computed> only for affected ids (changed + dependents)
 */
export function createColorEngine(colors, options = {}) {
  const contrastAlgorithm = options.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca';
  // Workspace color space: parsing, linked/contrast math, and contrast measurement
  // (APCA in this space; WCAG uses the same encoded bytes with the sRGB luminance formula).
  const colorSpace = options.colorSpace === 'srgb' ? 'srgb' : 'p3';
  const allColors = Array.isArray(colors) ? colors.slice() : [];
  const { dependents, colorById } = buildDependencyGraph(allColors);

  const cache = new Map(); // id -> { isValid, hex, oklch }

  const computeOne = (id, stack = new Set()) => {
    if (cache.has(id)) return cache.get(id);
    const color = colorById.get(id);
    if (!color) return invalidColor();
    if (stack.has(id)) {
      const invalid = invalidColor();
      cache.set(id, invalid);
      return invalid;
    }
    stack.add(id);

    if (color.mode === 'linked' && color.referenceColorId) {
      computeOne(color.referenceColorId, stack);
    } else if (color.mode === 'contrast') {
      if (color.backgroundColorId) computeOne(color.backgroundColorId, stack);
      if (color.foregroundColorId) computeOne(color.foregroundColorId, stack);
    }

    const result = computeColor(color, allColors, colorById, contrastAlgorithm, colorSpace, cache);
    cache.set(id, result);
    stack.delete(id);
    return result;
  };

  const computeAll = () => {
    const resultMap = new Map();
    for (const color of allColors) {
      resultMap.set(color.id, computeOne(color.id));
    }
    return resultMap;
  };

  const getAffectedBreadthFirst = (changedIds) => {
    const visited = new Set();
    const queue = Array.from(changedIds || []);
    for (const id of queue) visited.add(id);

    while (queue.length) {
      const current = queue.shift();
      const deps = dependents.get(current);
      if (!deps) continue;
      for (const next of deps) {
        if (!visited.has(next)) {
          visited.add(next);
          queue.push(next);
        }
      }
    }
    return visited;
  };

  const computeAffected = (changedIds) => {
    const affected = getAffectedBreadthFirst(changedIds);
    for (const id of affected) cache.delete(id);
    const resultMap = new Map();
    for (const id of affected) {
      resultMap.set(id, computeOne(id));
    }
    return resultMap;
  };

  return { computeAll, computeAffected };
}
